import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function HeroSection() {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToWorks = () => {
    const worksSection = document.getElementById('works-section');
    worksSection?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact-section');
    contactSection?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen overflow-hidden bg-gradient-to-br from-rose-900 via-fuchsia-900 to-orange-500">
      {/* Animated Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      {/* Dark Overlay for Better Contrast */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className={`text-lg font-semibold transition-colors ${
              scrolled ? 'text-gray-900' : 'text-white'
            }`}>Daniel Lozano</span>
          </div>
          
          <div className="hidden md:flex items-center gap-12">
            <button 
              onClick={scrollToWorks}
              className={`text-sm font-light transition-colors ${
                scrolled ? 'text-gray-700 hover:text-pink-500' : 'text-white hover:text-cyan-300'
              }`}
            >
              作品
            </button>
            <button 
              onClick={() => document.getElementById('about-section')?.scrollIntoView({ behavior: 'smooth' })}
              className={`text-sm font-light transition-colors ${
                scrolled ? 'text-gray-700 hover:text-pink-500' : 'text-white hover:text-cyan-300'
              }`}
            >
              について
            </button>
            <button 
              onClick={scrollToContact}
              className={`text-sm font-light transition-colors ${
                scrolled ? 'text-gray-700 hover:text-pink-500' : 'text-white hover:text-cyan-300'
              }`}
            >
              お問い合わせ
            </button>
          </div>

          <div className="flex items-center gap-2">
            <span className={`text-xs font-bold ${scrolled ? 'text-pink-500' : 'text-cyan-400'}`}>JP</span>
            <span className={`text-xs ${scrolled ? 'text-gray-400' : 'text-white/60'}`}>/</span>
            <button 
              onClick={() => navigate('/en')}
              className={`text-xs font-light transition-colors cursor-pointer ${scrolled ? 'text-gray-600 hover:text-pink-500' : 'text-white/80 hover:text-cyan-300'}`}
            >
              EN
            </button>
            <span className={`text-xs ${scrolled ? 'text-gray-400' : 'text-white/60'}`}>/</span>
            <button 
              onClick={() => navigate('/es')}
              className={`text-xs font-light transition-colors cursor-pointer ${scrolled ? 'text-gray-600 hover:text-pink-500' : 'text-white/80 hover:text-cyan-300'}`}
            >
              ES
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-8 pt-32 pb-20 min-h-screen flex items-center">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center w-full">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="inline-block">
              <span className="text-xs uppercase tracking-wider text-pink-400 border border-pink-400 px-4 py-2 rounded-full">
                グラフィックデザイナー × イラストレーター
              </span>
            </div>

            <h1 className="text-6xl lg:text-7xl font-bold text-white leading-tight">
              こんにちは、<span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-pink-400">ダニエル・ロサノ</span>です
            </h1>

            <h2 className="text-3xl lg:text-4xl font-semibold text-white/90 leading-tight">
              <span className="san-brainy">VTuber</span>ブランディング &<br />
              2Dアセットデザイナー
            </h2>

            <p className="text-lg text-gray-200 max-w-xl leading-relaxed">
              日本のエンターテインメント文化に根ざした高CTRビジュアル戦略、IP開発、ファンダム美学を専門としています。
              <span className="text-cyan-300 font-medium">スペイン出身のデザイナーとして、日本での生活・就労を強く希望しており、移住に積極的です</span>。西洋のクリエイティビティを日本のメディア産業に届けます。
            </p>

            <div className="flex flex-wrap gap-5 pt-4">
              <button
                onClick={scrollToWorks}
                className="px-10 py-4 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-semibold rounded-full shadow-lg hover:scale-105 transition-transform duration-300 whitespace-nowrap cursor-pointer"
              >
                作品を見る
              </button>
              <button
                onClick={scrollToContact}
                className="px-10 py-4 bg-transparent border-2 border-white text-white font-semibold rounded-full hover:bg-white hover:text-purple-900 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                お問い合わせ
              </button>
            </div>
          </div>

          {/* Right Visual */}
          <div className="relative">
            <div className="relative transform rotate-3 transition-transform duration-500 hover:rotate-0">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-400 to-pink-500 rounded-3xl blur-xl opacity-50"></div>
              <div className="relative rounded-3xl overflow-hidden shadow-2xl" style={{
                background: 'linear-gradient(135deg, #f97316 0%, #ec4899 35%, #a855f7 70%, #f43f5e 100%)'
              }}>
                <img
                  src="https://storage.readdy-site.link/project_files/ad497236-385f-4b6e-bb69-bc2f03e1f0bd/33769221-865a-4957-8c21-7f121a86cfe0_a544eaf5be772e2148964676b3d4639d-1.webp?v=4a2144e5bd6d7dda4b79b4943a632705"
                  alt="Daniel Lozano - VTuberキャラクターデザイン"
                  className="w-full h-auto object-cover object-top"
                  fetchPriority="high"
                  decoding="async"
                  style={{
                    filter: 'saturate(1.1) contrast(1.02) brightness(1.01)',
                    mixBlendMode: 'normal',
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
